const DASHBOARD_VIEW_OPTIONS = [
    { value: 'Collector View', label: 'Collector View' },
    { value: 'Collections Manager View', label: 'Collections Manager View' },
];

const PERMISSION_META_KEYS = new Set([
    'UserName',
    'userName',
    'ModifiedBy',
    'ModifiedOn',
    'IsKeyMachineAdmin',
]);

/** Matches the legacy Login & Permission tab: Navigation Items + Submenus */
const PERMISSION_MODULES = [
    {
        id: 'inventory',
        title: 'Inventory',
        navKey: 'NV_Inventory',
        navDescription: 'Shows the Inventory module in the sidebar. Without it, Inventory disappears and redirects to /home.',
        permissions: [
            { key: 'Inv_Add', label: 'Add Vehicle', description: 'Allows adding vehicles. Without it, the + Add Vehicle button disappears or is disabled.' },
            {
                key: 'Inv_Edit',
                label: 'Edit Vehicle & Customize List Page',
                description: 'Allows editing vehicles and customizing the list page. Without it, Edit becomes unavailable.',
            },
            { key: 'Inv_Del', label: 'Delete Vehicle', description: 'Allows deleting vehicles. Without it, Delete becomes unavailable.' },
            { key: 'Inv_Flag', label: 'Flags', description: 'Allows managing visual alerts/flags on vehicles. Without it, the flag selector disappears.' },
            {
                key: 'Inv_Cost',
                label: 'Show Cost',
                description: 'Shows unit costs (affects Accounting). Without it, Unit Cost column and Cost & Accounting tab disappear.',
            },
            {
                key: 'Inv_AddEditPurchasedFrom',
                label: 'Add/Edit Purchased From',
                description: 'Allows registering and modifying vehicle purchase origin. Without it, vendor/purchase fields are read-only.',
            },
            {
                key: 'Inv_ChangeLot',
                label: 'Change Lot Name',
                description: 'Allows transferring vehicles between branches. Without it, Transfer Lot disappears.',
            },
            {
                key: 'Inv_AddRemoveTag',
                label: 'Add/Remove Tag',
                description: 'Allows adding or removing operational tags. Without it, tag add/remove controls disappear.',
            },
            {
                key: 'Inv_MarketComps',
                label: 'Market Comps',
                description: 'Allows viewing market price comparisons. Without it, Market Comps tab/widget disappears.',
            },
            { key: 'Inv_Appraisals', label: 'Appraisals', description: 'Allows viewing appraisals. Without it, Appraisals tab/widget disappears.' },
            { key: 'Inv_EditNotes', label: 'Edit Notes', description: 'Allows editing quick notes. Without it, the edit icon in quick notes disappears.' },
        ],
    },
    {
        id: 'deals',
        title: 'Deals',
        navKey: 'NV_Sales',
        navDescription: 'Shows the Deals module in the sidebar. Without it, Deals disappears from navigation.',
        permissions: [
            { key: 'Sales_Add', label: 'Add Deal', description: 'Allows creating deals. Without it, + New Deal disappears.' },
            { key: 'Sales_Edit', label: 'Edit Deal', description: 'Allows editing deals. Without it, edit buttons and We-Owe become unavailable.' },
            { key: 'Sales_FullEdit', label: 'Full Deal Editing', description: 'Allows full deal editing. Without it, advanced edit actions are blocked.' },
            { key: 'Sales_Del', label: 'Delete Deal', description: 'Allows deleting deals. Without it, Delete Deal disappears.' },
            {
                key: 'Sales_Trans',
                label: 'Transfer',
                description: 'Allows advancing deal stage and posting to accounting. Without it, stage selector and Post to Accounting disappear.',
            },
            {
                key: 'Sales_Fund',
                label: 'Funding',
                description: 'Allows modifying funding/financing status (affects BHPH). Without it, Funding Status control disappears.',
            },
            { key: 'Sales_Flag', label: 'Flags', description: 'Allows assigning visual priorities to sales. Without it, the flag selector disappears.' },
            {
                key: 'Sales_Approval',
                label: 'Deals Approval',
                description: 'Allows approving sales and sending to lenders (RFC). Without it, Pending Approval and Transfer to RFC disappear.',
            },
            {
                key: 'Sales_Restore_Deleted',
                label: 'Restore Deleted Deal',
                description: 'Allows recovering deleted deals. Without it, Restore Deal disappears.',
            },
            {
                key: 'Sales_ChangeLot',
                label: 'Change Lot Name',
                description: 'Allows transferring the deal to another lot. Without it, Transfer Lot disappears.',
            },
            {
                key: 'Sales_SendToTagAgency',
                label: 'Send to Tag Agency',
                description: 'Allows sending documentation to the tag agency. Without it, that option disappears.',
            },
            {
                key: 'Sales_MenuContracts',
                label: 'F&I Menu Contracts',
                description: 'Allows F&I menu contract generation. Without it, Generate Contracts disappears.',
            },
            {
                key: 'Sales_MenuSettingsLogs',
                label: 'F&I Menu Settings/Logs',
                description: 'Allows F&I menu settings/logs access. Without it, Settings tab disappears.',
            },
            {
                key: 'Sales_MenuDealerCost',
                label: 'Dealer Cost Menu',
                description: 'Allows viewing F&I dealer cost. Without it, Dealer Cost columns disappear.',
            },
            { key: 'DealerTrack', label: 'Dealer Track', description: 'Allows Deal integration with DealerTrack. Without it, Send to DealerTrack disappears.' },
            {
                key: 'Sales_ShowWashout',
                label: 'Show Washout',
                description: 'Shows real sale profitability/recap (affects Accounting/BHPH). Without it, Recap/Washout disappears.',
            },
            {
                key: 'Sales_ChangeSoldDate',
                label: 'Change Sold Date',
                description: 'Allows altering the official sold date on forms. Without it, Sold Date is read-only.',
            },
            {
                key: 'Sales_ChangeFinanceManager',
                label: 'Change Finance Manager',
                description: 'Allows reassigning the finance manager (affects Payroll). Without it, Finance Manager is read-only.',
            },
            {
                key: 'Sales_OverrideCommissions',
                label: 'Override Commissions',
                description: 'Allows modifying commissions (affects Payroll). Without it, commission inputs are read-only.',
            },
            {
                key: 'Sales_DeleteDocument',
                label: 'Delete Deal Documents',
                description: 'Allows deleting attached deal files. Without it, the trash icon disappears.',
            },
            {
                key: 'Sales_CreditPull',
                label: 'Credit Pull',
                description: 'Allows requesting credit reports from the deal. Without it, Pull Credit disappears.',
            },
        ],
    },
    {
        id: 'dashboard',
        title: 'Dashboard',
        navKey: 'NV_Dashboard',
        navDescription: 'Shows the Dashboard module in the sidebar. Without it, Dashboard disappears.',
        permissions: [
            {
                key: 'Dashboard_PartsList',
                label: 'Parts List',
                description: 'Shows required parts widget on the dashboard (affects Parts). Without it, the panel disappears.',
            },
            {
                key: 'Dashboard_ReconApproval',
                label: 'Recon Approval',
                description: 'Shows pending recon approvals widget. Without it, the approvals panel disappears.',
            },
        ],
    },
    {
        id: 'desking',
        title: 'Desking Tools',
        navKey: 'NV_Desking',
        navDescription: 'Shows Desking Tools in the sidebar. Without it, Desking disappears.',
        permissions: [],
    },
    {
        id: 'bhph',
        title: 'BHPH',
        navKey: 'NV_Payments',
        navDescription: 'Shows BHPH/Collections in the sidebar. Without it, Collections/BHPH disappears.',
        permissions: [
            {
                key: 'Pay_Add',
                label: 'Take Payment',
                description: 'Allows taking payments (affects Accounting balances). Without it, Take Payment disappears.',
            },
            { key: 'Pay_Edit', label: 'Edit Payment', description: 'Allows editing payments. Without it, Edit Payment disappears.' },
            { key: 'Pay_Del', label: 'Delete Payment', description: 'Allows deleting payments. Without it, Delete Payment disappears.' },
            { key: 'BHPH_AllowReversePayment', label: 'Reverse Payment', description: 'Allows reversing payments. Without it, Reverse Payment disappears.' },
            { key: 'BHPH_EditPtp', label: 'Edit PTP', description: 'Allows editing Promises to Pay. Without it, Edit PTP options disappear.' },
            { key: 'BHPH_CancelPtp', label: 'Cancel PTP', description: 'Allows canceling Promises to Pay. Without it, Cancel PTP disappears.' },
            {
                key: 'BHPH_EditEmailContent',
                label: 'Communication - Edit Email Content',
                description: 'Allows editing customer email templates. Without it, templates are read-only.',
            },
            {
                key: 'BHPH_SetRunTimeForScheduledPayments',
                label: 'Can Set Run Time for Scheduled Payments',
                description: 'Allows adjusting scheduled payment run time. Without it, the time selector disappears.',
            },
            {
                key: 'BHPH_Portfolio_Change',
                label: 'Portfolio - Change',
                description: 'Allows reassigning collection portfolios. Without it, portfolio dropdown is read-only.',
            },
            {
                key: 'BHPH_ServicingBranch_Change',
                label: 'Servicing Branch - Change',
                description: 'Allows changing servicing branch. Without it, branch dropdown is read-only.',
            },
            {
                key: 'BHPH_ForceReQueue',
                label: 'Allow Force Re-queue',
                description: 'Allows forcing accounts into the call queue. Without it, force re-queue disappears.',
            },
            { key: 'BHPH_AddRemoveTag', label: 'Add/Remove Tag', description: 'Allows managing collection tags. Without it, tag management disappears.' },
            {
                key: 'BHPH_AllowRefinance',
                label: 'Allow Refinance',
                description: 'Allows refinance/debt restructuring. Without it, refinance actions disappear.',
            },
            { key: 'BHPH_AllowOFR', label: 'Allow OFR', description: 'Allows OFR transfers. Without it, OFR actions disappear.' },
            { key: 'BHPH_AllowChargeOff', label: 'Allow Charge Off', description: 'Allows portfolio charge-offs. Without it, Charge Off actions disappear.' },
            {
                key: 'BHPH_ReverseChargeOff',
                label: 'Reverse Charge Off',
                description: 'Allows reversing charge-offs. Without it, Reverse Charge Off disappears.',
            },
        ],
    },
    {
        id: 'crm',
        title: 'CRM',
        navKey: 'NV_CRM',
        navDescription: 'Shows the CRM module in the sidebar. Without it, CRM disappears.',
        permissions: [
            { key: 'Cust_Add', label: 'Add Customer', description: 'Allows adding customers/leads.' },
            { key: 'Cust_Edit', label: 'Edit Customer', description: 'Allows editing customers/leads.' },
            { key: 'Cust_Del', label: 'Delete Customer', description: 'Allows deleting customers/leads.' },
            { key: 'Cust_Flag', label: 'Flags', description: 'Allows managing CRM flags.' },
            { key: 'Cust_Circle', label: 'Circles', description: 'Allows managing CRM circles.' },
            { key: 'CRM_RecordManager', label: 'Allow to change Rec. Manager', description: 'Allows changing the record manager on opportunities.' },
            { key: 'CRM_Salesman', label: 'Allow to change Salesman', description: 'Allows changing the salesman on opportunities.' },
            { key: 'CRM_LostLead', label: 'Allow to check lost Lead', description: 'Allows marking/checking lost leads.' },
            { key: 'CRM_NewLead', label: 'Allow to check new Lead', description: 'Allows checking new leads.' },
            { key: 'CRM_Emails_SMS', label: 'Add/Edit Message Templates', description: 'Allows adding/editing message templates.' },
            { key: 'CRM_DuplicateLead', label: 'Allow Duplicate', description: 'Allows creating duplicate leads.' },
            { key: 'CRM_AllLead', label: 'All lead access', description: 'Allows access to all leads.' },
            { key: 'CRM_AllLotsLead', label: 'All lots lead access', description: 'Allows lead access across all lots.' },
            { key: 'CRM_AddEditTags', label: 'Add/Edit Tag', description: 'Allows adding/editing CRM tags.' },
            { key: 'CRM_CreditPull', label: 'Credit Pull', description: 'Allows credit pulls from CRM.' },
            { key: 'CRM_CustomerMerge', label: 'Allow Customer Merge', description: 'Allows merging customers.' },
        ],
    },
    {
        id: 'hr',
        title: 'HR',
        navKey: 'NV_HR',
        navDescription: 'Shows the HR module in the sidebar. Without it, HR disappears.',
        permissions: [
            {
                key: 'Set_User',
                label: 'Employee Info',
                description: 'Allows basic employee information management. Without it, employee creation/settings become unavailable.',
            },
            { key: 'HR_Setting', label: 'HR Settings', description: 'Allows HR settings configuration. Without it, settings tabs disappear.' },
            { key: 'HR_Delete', label: 'Delete', description: 'Allows deleting employees. Without it, delete buttons disappear.' },
            { key: 'HR_Calendar', label: 'Calendar', description: 'Allows HR calendar administration. Without it, scheduling calendar tools disappear.' },
            {
                key: 'HR_PayInfo',
                label: 'Pay Information',
                description: 'Allows pay information access (affects Payroll). Without it, pay rate inputs become read-only/hidden.',
            },
            {
                key: 'HR_EditWorkScheduleAll',
                label: 'Edit All Work Schedule',
                description: 'Allows editing all work schedules. Without it, all-schedule editing tools disappear.',
            },
            {
                key: 'HR_EditWorkSchedule',
                label: 'Edit Work Schedule',
                description: 'Allows editing work schedules. Without it, schedule tools disappear or become read-only.',
            },
            {
                key: 'HR_LogIn_Permissions_Tab',
                label: 'Permissions',
                description: 'Allows changing Login & Permissions for other users. Without it, the Permissions tab disappears.',
            },
            {
                key: 'HR_RevealSensitiveData',
                label: 'Reveal Sensitive Data',
                description: 'Allows viewing confidential HR data uncensored. Without it, sensitive data stays masked.',
            },
        ],
    },
    {
        id: 'payroll',
        title: 'Payroll',
        navKey: null,
        permissions: [
            { key: 'HR_ApproveHours', label: 'Approve Hours', description: 'Allows approving worked hours. Without it, hour approval buttons disappear.' },
            {
                key: 'HR_ApproveCommissions',
                label: 'Approve Commissions',
                description: 'Allows approving commissions. Without it, commission approval options disappear.',
            },
            {
                key: 'HR_MarkCommissionsAsPaid',
                label: 'Mark Commissions as Paid',
                description: 'Allows marking commissions as paid. Without it, payout options disappear.',
            },
            { key: 'HR_PayrollChecks', label: 'Payroll Checks', description: 'Allows payroll checks generation. Without it, Generate Checks disappears.' },
        ],
    },
    {
        id: 'service',
        title: 'Service',
        navKey: 'NV_Service',
        navDescription: 'Shows the Service module in the sidebar. Without it, Service disappears.',
        permissions: [
            { key: 'Service_OpenRO', label: 'Open RO', description: 'Allows opening repair orders.' },
            { key: 'Service_DeleteCust', label: 'Delete Customers', description: 'Allows deleting service customers.' },
            { key: 'Service_EditRO', label: 'Edit RO', description: 'Allows editing repair orders.' },
            { key: 'Service_VoidRO', label: 'Void RO', description: 'Allows voiding repair orders.' },
            { key: 'Service_TransferRO', label: 'Transfer RO', description: 'Allows transferring repair orders.' },
            { key: 'Service_AddDeleteParts', label: 'Add Parts', description: 'Allows adding parts on ROs.' },
            { key: 'Service_RemoveParts', label: 'Remove Parts', description: 'Allows removing parts from ROs.' },
            { key: 'Service_LaborHours', label: 'Labor Hours', description: 'Allows managing labor hours.' },
            { key: 'Service_OverrideLaborRate', label: 'Override Labor Rate', description: 'Allows overriding labor rates.' },
            { key: 'Service_OverridePartPrice', label: 'Override Part Price', description: 'Allows overriding part prices on ROs.' },
            { key: 'Service_OverrideSubletPrice', label: 'Override Sublet Price', description: 'Allows overriding sublet prices.' },
            { key: 'Service_Advisor', label: 'Edit Advisor', description: 'Allows editing the service advisor.' },
            { key: 'Service_Flag', label: 'Flags', description: 'Allows managing service flags.' },
            { key: 'Service_ViewOperationCode', label: 'View Operation Codes', description: 'Allows viewing operation codes.' },
            { key: 'Service_AddOperationCode', label: 'Add Operation Codes', description: 'Allows adding operation codes.' },
            { key: 'Service_EditOperationCode', label: 'Edit Operation Codes', description: 'Allows editing operation codes.' },
            { key: 'Service_DeleteOperationCode', label: 'Delete Operation Codes', description: 'Allows deleting operation codes.' },
        ],
    },
    {
        id: 'parts',
        title: 'Parts',
        navKey: 'NV_Parts',
        navDescription: 'Shows the Parts module in the sidebar. Without it, Parts disappears.',
        permissions: [
            { key: 'Parts_AddPart', label: 'Add Part', description: 'Allows adding parts to inventory. Without it, + New Part disappears.' },
            { key: 'Parts_EditPart', label: 'Edit Part', description: 'Allows editing parts. Without it, part edit controls disappear.' },
            { key: 'Parts_NewTicket', label: 'New Ticket', description: 'Allows creating counter tickets. Without it, + New Ticket disappears.' },
            { key: 'Parts_EditTicket', label: 'Edit Ticket', description: 'Allows editing tickets. Without it, ticket edit controls disappear.' },
            { key: 'Parts_TicketAddDeleteParts', label: 'Add/Delete Parts on Ticket', description: 'Allows adding/deleting parts on a ticket.' },
            {
                key: 'Parts_OverridePartPrice',
                label: 'Override Part Price',
                description: 'Allows overriding part prices (affects Service/Accounting). Without it, override inputs disappear.',
            },
            {
                key: 'Parts_ChangeQuantity',
                label: 'Change Parts Quantity',
                description: 'Allows changing parts quantity/stock. Without it, stock adjustment buttons disappear.',
            },
            { key: 'Parts_Flag', label: 'Flags', description: 'Allows managing parts flags.' },
        ],
    },
    {
        id: 'accounting',
        title: 'Accounting',
        navKey: 'NV_Cashier',
        navDescription: 'Shows the Accounting module in the sidebar. Without it, Accounting disappears.',
        permissions: [
            {
                key: 'Cashier_Collect',
                label: 'Collection and Payouts',
                description: 'Allows collection and payouts. Without it, payout/batchout tools disappear.',
            },
            { key: 'Cashier_Acct', label: 'Open Balances', description: 'Allows open balances access. Without it, open balances tools disappear.' },
            { key: 'Cashier_EditTrans', label: 'Edit Transaction', description: 'Allows editing cashier transactions. Without it, edit menus disappear.' },
            {
                key: 'Cashier_DelTrans',
                label: 'Delete Transaction',
                description: 'Allows deleting cashier transactions. Without it, void/delete menus disappear.',
            },
            {
                key: 'Cashier_AssignTrans',
                label: 'Assign Transaction',
                description: 'Allows assigning transactions/payments. Without it, assignment tools disappear.',
            },
            { key: 'Accounting_Receivable', label: 'Receivable', description: 'Allows Accounts Receivable access. Without it, AR disappears from the menu.' },
            { key: 'Accounting_Payable', label: 'Payable', description: 'Allows Accounts Payable access. Without it, AP disappears from the menu.' },
            { key: 'Accounting_Payroll', label: 'Payroll', description: 'Allows accounting payroll tools access.' },
            { key: 'Accounting_Journals', label: 'Journals', description: 'Allows general journal access. Without it, Journals disappear.' },
            { key: 'Accounting_COA', label: 'COA', description: 'Allows Chart of Accounts access. Without it, COA disappears.' },
            { key: 'Accounting_Tools', label: 'Tools', description: 'Allows accounting tools/settings access. Without it, Tools disappear.' },
            { key: 'Accounting_LockDate', label: 'Lock Date', description: 'Allows fiscal lock date management. Without it, Lock Date tools disappear.' },
            { key: 'Accounting_Banking', label: 'Banking - View', description: 'Allows viewing Banking. Without it, Banking is hidden.' },
            { key: 'Banking_AddCheck', label: 'Banking - Add Check', description: 'Allows adding checks.' },
            { key: 'Banking_EditCheck', label: 'Banking - Edit Check', description: 'Allows editing checks.' },
            { key: 'Banking_VoidCheck', label: 'Banking - Void Check', description: 'Allows voiding checks.' },
            { key: 'Banking_PrintCheck', label: 'Banking - Print Check', description: 'Allows printing checks.' },
            { key: 'Banking_ReprintCheck', label: 'Banking - Reprint Check', description: 'Allows reprinting checks.' },
            { key: 'Banking_ApproveCheck', label: 'Banking - Approve Check', description: 'Allows approving checks.' },
            { key: 'Banking_UnapproveCheck', label: 'Banking - Unapprove Check', description: 'Allows unapproving checks.' },
            { key: 'Banking_AddDeposit', label: 'Banking - Add Deposit', description: 'Allows adding deposits.' },
            { key: 'Banking_EditDeposit', label: 'Banking - Edit Deposit', description: 'Allows editing deposits.' },
            { key: 'Banking_VoidDeposit', label: 'Banking - Void Deposit', description: 'Allows voiding deposits.' },
            { key: 'Banking_TransferFund', label: 'Banking - Transfer Fund', description: 'Allows transferring funds.' },
            {
                key: 'Banking_ViewEndingBalance',
                label: 'Banking - View Ending Balance',
                description: 'Allows viewing ending balance. Without it, ending balance column is hidden.',
            },
        ],
    },
    {
        id: 'settings',
        title: 'Settings',
        navKey: 'NV_Settings',
        navDescription: 'Shows the Settings module in the sidebar. Without it, Settings disappears.',
        permissions: [
            { key: 'Set_Comp', label: 'Company Info', description: 'Allows company information settings. Without it, Company Info tab disappears.' },
            { key: 'Set_Lots', label: 'Lots', description: 'Allows lots/branch settings. Without it, Lots tab disappears.' },
            { key: 'Setting_Access_Query', label: 'Access Queries', description: 'Allows access to queries settings.' },
            { key: 'Setting_ManageTasks', label: 'Manage Tasks', description: 'Allows managing tasks / bulletin board settings.' },
            { key: 'Setting_Inv_AddTags', label: 'Inventory - Add Tags', description: 'Allows adding inventory tags in global catalog.' },
            { key: 'Setting_Inv_DeleteTags', label: 'Inventory - Delete Tags', description: 'Allows deleting inventory tags in global catalog.' },
            { key: 'Setting_BHPH_AddTags', label: 'BHPH - Add Tags', description: 'Allows adding BHPH tags globally.' },
            { key: 'Setting_BHPH_DeleteTags', label: 'BHPH - Delete Tags', description: 'Allows deleting BHPH tags globally.' },
            { key: 'Setting_HomePage', label: 'Home Page', description: 'Allows home page / bulletin board configuration.' },
            { key: 'Setting_Recon', label: 'Recon', description: 'Allows recon process settings. Without it, Recon settings disappear.' },
            { key: 'Setting_BIAndGoals', label: 'BI and Goals', description: 'Allows BI and goals configuration. Without it, BI goal tabs disappear.' },
            { key: 'Setting_CRMTags', label: 'CRM - Tags', description: 'Allows CRM master tag configuration. Without it, CRM tag panel disappears.' },
            { key: 'Setting_BHPH_EditContracts', label: 'BHPH - Edit Contracts', description: 'Allows editing BHPH contract templates.' },
        ],
    },
    {
        id: 'reports',
        title: 'Reports',
        navKey: 'NV_Reports',
        navDescription: 'Shows the Reports module in the sidebar. Without it, Reports disappears.',
        permissions: [
            { key: 'Reports_Inventory', label: 'Inventory', description: 'Allows Inventory reports section access.' },
            { key: 'Reports_Deals', label: 'Deals', description: 'Allows Deals reports section access.' },
            { key: 'Reports_CRM', label: 'CRM', description: 'Allows CRM reports section access.' },
            { key: 'Reports_FNI', label: 'F&I', description: 'Allows F&I reports section access.' },
            { key: 'Reports_Accounting', label: 'Accounting', description: 'Allows Accounting reports section access.' },
            { key: 'Accounting_Financials', label: 'Financials', description: 'Allows Financials reports section access.' },
            { key: 'Accounting_BankingTransactions', label: 'Banking Transactions', description: 'Allows Banking Transactions reports access.' },
            { key: 'Accounting_CheckingTransactions', label: 'Checking Transactions', description: 'Allows Checking Transactions reports access.' },
            { key: 'Reports_BHPH', label: 'BHPH', description: 'Allows BHPH reports section access.' },
            { key: 'Reports_Service', label: 'Service', description: 'Allows Service reports section access.' },
            { key: 'Reports_Parts', label: 'Parts', description: 'Allows Parts reports section access.' },
            { key: 'Reports_Title', label: 'Title', description: 'Allows Title reports section access.' },
            { key: 'Reports_Documents', label: 'Documents', description: 'Allows Documents reports section access.' },
            { key: 'Reports_HR', label: 'HR', description: 'Allows HR reports section access.' },
            { key: 'Reports_Payroll', label: 'Payroll', description: 'Allows Payroll reports section access.' },
        ],
    },
    {
        id: 'creditReporting',
        title: 'Credit Reporting',
        navKey: 'NV_CreditReporting',
        navDescription: 'Shows Credit Reporting in the sidebar. Without it, Credit Reporting disappears.',
        permissions: [
            { key: 'CreditReporting_Edit', label: 'Edit', description: 'Allows editing credit reporting runs.' },
            { key: 'CreditReporting_EditDoNotReport', label: 'Edit Do Not Report', description: 'Allows editing Do Not Report settings.' },
            { key: 'CreditReporting_ApproveCreditRun', label: 'Approve Credit Run', description: 'Allows approving monthly credit bureau runs.' },
            { key: 'CreditReporting_DownloadMetro2Files', label: 'Download Metro 2 Files', description: 'Allows downloading Metro 2 files.' },
            { key: 'CreditReporting_UnlockCreditRun', label: 'Unlock Credit Run', description: 'Allows unlocking credit runs.' },
            { key: 'CreditReporting_AccountView', label: 'Account - View', description: 'Allows viewing account-level credit reporting.' },
            { key: 'CreditReporting_AccountEdit', label: 'Account - Edit', description: 'Allows editing account-level credit reporting.' },
            { key: 'CreditReporting_AccountViewHistory', label: 'Account - View History', description: 'Allows viewing account reporting history.' },
            { key: 'CreditReporting_AccountEditDoNotReport', label: 'Account - Edit Do Not Report', description: 'Allows editing account Do Not Report.' },
            {
                key: 'CreditReporting_ChangeReportingCompanyOnPortfolio',
                label: 'Portfolio - Change Credit Reporting Company',
                description: 'Allows changing credit reporting company on portfolio.',
            },
            {
                key: 'CreditReporting_CompanySettingView',
                label: 'View Credit Report Company Setting',
                description: 'Allows viewing credit report company settings.',
            },
            {
                key: 'CreditReporting_CompanySettingEdit',
                label: 'Edit Credit Report Company Setting',
                description: 'Allows editing credit report company settings.',
            },
            { key: 'CreditReporting_DeleteLastRun', label: 'Delete Last Run', description: 'Allows deleting the last credit run.' },
        ],
    },
    {
        id: 'recon',
        title: 'Recon',
        navKey: 'NV_Recon',
        navDescription: 'Shows the Recon module in the sidebar. Without it, Recon disappears.',
        permissions: [
            {
                key: 'Recon_ApproveActivity',
                label: 'Approval',
                description: 'Allows approving recon estimates/activities. Without it, approval buttons disappear.',
            },
            {
                key: 'Recon_RepairAssignee',
                label: 'Repair Assignee',
                description: 'Allows assigning mechanics/repair owners. Without it, assignee dropdowns disappear.',
            },
        ],
    },
    {
        id: 'bi',
        title: 'Business Intelligence',
        navKey: 'NV_BI',
        navDescription: 'Shows Business Intelligence in the sidebar. Without it, BI disappears.',
        permissions: [
            { key: 'BI_Dashboard_View', label: 'Dashboard View', description: 'Allows viewing BI dashboards.' },
            { key: 'BI_Dashboard_Edit', label: 'Dashboard Edit', description: 'Allows editing BI dashboards.' },
            { key: 'BI_CustomReports_View', label: 'Custom Reports View', description: 'Allows viewing custom BI reports.' },
            { key: 'BI_Rules_View', label: 'Rules View', description: 'Allows viewing BI rules.' },
            { key: 'BI_Query_View', label: 'Query View', description: 'Allows viewing BI queries.' },
            { key: 'BI_Query_Edit', label: 'Query Edit', description: 'Allows editing BI queries/builders.' },
        ],
    },
    {
        id: 'websiteSettings',
        title: 'Website Settings',
        navKey: null,
        showNavCheckbox: false,
        permissions: [
            {
                key: 'Website_Settings',
                label: 'Administrator',
                description: 'Allows website administrator/configuration access. Without it, website settings disappear.',
            },
        ],
    },
    {
        id: 'electronicPayments',
        title: 'Electronic Payments',
        navKey: null,
        showNavCheckbox: false,
        permissions: [
            {
                key: 'ElectronicPayment_DeletePendingTransactions',
                label: 'Delete Pending Transactions',
                description: 'Allows deleting pending electronic transactions (affects Accounting).',
            },
        ],
    },
    {
        id: 'training',
        title: 'Training',
        navKey: null,
        showNavCheckbox: false,
        permissions: [
            { key: 'Training_ManageCategories', label: 'Manage Categories', description: 'Allows managing training categories.' },
            { key: 'Training_ManageVideos', label: 'Manage Videos', description: 'Allows managing training videos.' },
        ],
    },
];

const getModulePermissionKeys = (module) => {
    const keys = module.permissions.map((item) => item.key);
    if (module.navKey) keys.unshift(module.navKey);
    return keys;
};

const getAllCatalogKeys = () => PERMISSION_MODULES.flatMap(getModulePermissionKeys);

const getPermissionModules = () => PERMISSION_MODULES;

const getPermissionValue = (permissions = {}, key) => Boolean(permissions?.[key]);

const isModuleNavChecked = (permissions, module) => {
    if (module.navKey) {
        return getPermissionValue(permissions, module.navKey);
    }
    if (!module.permissions.length) return false;
    return module.permissions.every((item) => getPermissionValue(permissions, item.key));
};

export {
    DASHBOARD_VIEW_OPTIONS,
    PERMISSION_MODULES,
    PERMISSION_META_KEYS,
    getPermissionModules,
    getModulePermissionKeys,
    getAllCatalogKeys,
    getPermissionValue,
    isModuleNavChecked,
};
